import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ListChecks, Plus, Trash2, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Admin editor for the saved quote-gate justifications reps can pick when acknowledging flagged factors. */
export function JustificationLibraryEditor() {
  const [items, setItems] = useState(null);
  const [usage, setUsage] = useState({});
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API_URL}/api/quote-gate/justifications`, { withCredentials: true })
      .then((r) => { setItems(r.data.items || []); setUsage(r.data.usage || {}); }).catch(() => setItems([]));
  }, []);
  const save = async (next) => {
    setBusy(true);
    try {
      const r = await axios.put(`${API_URL}/api/quote-gate/justifications`, { items: next }, { withCredentials: true });
      setItems(r.data.items); setDraft('');
      toast.success(`${r.data.items.length} saved justification(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save justifications'); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3" data-testid="justification-library">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-[#0F2040]">
        <ListChecks className="w-3.5 h-3.5 text-violet-500" /> Quote gate justifications
        <span className="text-[10px] font-normal text-slate-400">· reps pick one when confirming “Factors reviewed”</span>
      </div>
      {!items && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400 mt-1" />}
      {items && (
        <ul className="mt-1.5 divide-y divide-slate-100 rounded border border-slate-200 max-h-36 overflow-y-auto">
          {items.length === 0 && <li className="px-2.5 py-1.5 text-[11px] text-slate-400" data-testid="justification-empty">No saved justifications — reps will type their own.</li>}
          {items.map((t, i) => (
            <li key={t} className="flex items-center justify-between gap-2 px-2.5 py-1 text-[11px]" data-testid={`justification-row-${i}`}>
              <span className="text-slate-700 truncate">{t}</span>
              <span className="flex items-center gap-2 shrink-0">
                {usage[t] > 0 && <span className="text-[10px] text-slate-400">used {usage[t]}×</span>}
                <button type="button" onClick={() => save(items.filter((x) => x !== t))} disabled={busy} className="text-slate-300 hover:text-rose-500" title="Remove" data-testid={`justification-remove-${i}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2 mt-1.5">
        <Input value={draft} onChange={(e) => setDraft(e.target.value.slice(0, 200))} placeholder="Add a justification, e.g. Client-mandated go-live date"
          className="h-7 text-xs flex-1" data-testid="justification-input" onKeyDown={(e) => { if (e.key === 'Enter' && draft.trim()) save([...(items || []), draft.trim()]); }} />
        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy || !draft.trim()} onClick={() => save([...(items || []), draft.trim()])} data-testid="justification-add-btn">
          <Plus className="w-3 h-3 mr-1" /> Add
        </Button>
      </div>
    </div>
  );
}
